import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCrudItem } from "../../../hooks/useCrud"
import type { AdminUser, AdminUserFormValues, UserRoles } from "../models/adminUser"
import { AdminUserSchema, UserRolesSchema } from "../models/adminUser"
import { adminUserApi } from "../services/adminUserApi"

const BASE_KEY = "admin-users"

export function useAdminUser(id?: number | null) {
  return useCrudItem<AdminUser>(BASE_KEY, BASE_KEY, AdminUserSchema, id)
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, AdminUserFormValues>({
    mutationFn: (values) => adminUserApi.create(values) as Promise<AdminUser>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateAdminUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, { id: number; values: Partial<AdminUserFormValues> }>({
    mutationFn: ({ id, values }) => adminUserApi.update(id, values) as Promise<AdminUser>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => adminUserApi.delete(id) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useActivateAdminUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, number>({
    mutationFn: (id) => adminUserApi.activate(id) as Promise<AdminUser>,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, id] })
    },
  })
}

export function useDeactivateAdminUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, number>({
    mutationFn: (id) => adminUserApi.deactivate(id) as Promise<AdminUser>,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, id] })
    },
  })
}

export function useResetPassword() {
  return useMutation<void, Error, { id: number; password: string }>({
    mutationFn: ({ id, password }) => adminUserApi.resetPassword(id, password) as Promise<void>,
  })
}

export function useReset2fa() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => adminUserApi.reset2fa(id) as Promise<void>,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, id] })
    },
  })
}

export function useAdminUserRoles(id?: number | null) {
  return useQuery<UserRoles>({
    queryKey: [BASE_KEY, "roles", id],
    queryFn: () => adminUserApi.getRoles(id!) as Promise<UserRoles>,
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  })
}

export function useAssignAdminUserRoles() {
  const queryClient = useQueryClient()
  return useMutation<UserRoles, Error, { id: number; assign: string[]; revoke: string[] }>({
    mutationFn: ({ id, assign, revoke }) => adminUserApi.assignRoles(id, { assign, revoke }) as Promise<UserRoles>,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "roles", vars.id] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}
